import React, { useState, useMemo, useEffect } from 'react';
import { Table, Form, InputGroup, Button, Row, Col, Pagination, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import TPinModal from '../common/TPinModal';
import { showToast, toastMessages } from '../../utils/toast';

const TopUpRequestTable = ({
  data,
  onDataChange,
  searchValue = '',
  onSearch,
  fromDate,
  toDate,
  onDateChange,
  pagination = {},
  onPageChange,
  onPageSizeChange,
  loading = false,
  allowCreateButton = true,
  showActions = true,
  denseRows = false,
}) => {
  const navigate = useNavigate();
  
  // State management
  const [searchQuery, setSearchQuery] = useState(searchValue || '');
useEffect(() => {
  setSearchQuery(searchValue || '');
}, [searchValue]);

const [showDateRangeModal, setShowDateRangeModal] = useState(false);
const [pendingRange, setPendingRange] = useState({
  start: fromDate || '',
  end: toDate || '',
});

useEffect(() => {
  setPendingRange({
    start: fromDate || '',
    end: toDate || '',
  });
}, [fromDate, toDate]);

const {
  totalRecords = Array.isArray(data) ? data.length : 0,
  pageSize = 10,
  currentPage: currentPageProp = 0,
  totalPages: totalPagesProp,
} = pagination || {};

const safeCurrentPage = Number.isFinite(currentPageProp) && currentPageProp >= 0
  ? currentPageProp
  : 0;

const totalPages = totalPagesProp || Math.max(Math.ceil(totalRecords / pageSize), 1);

const buildPageList = () => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index);
  }

  const pages = [];
  const firstPage = 0;
  const lastPage = totalPages - 1;
  const rangeStart = Math.max(safeCurrentPage - 2, firstPage + 1);
  const rangeEnd = Math.min(safeCurrentPage + 2, lastPage - 1);

  pages.push(firstPage);

  if (rangeStart > firstPage + 1) {
    pages.push('start-ellipsis');
  }

  for (let page = rangeStart; page <= rangeEnd; page += 1) {
    pages.push(page);
  }

  if (rangeEnd < lastPage - 1) {
    pages.push('end-ellipsis');
  }

  pages.push(lastPage);

  return pages;
};

const paginationPages = buildPageList();

  const handleSearchInputChange = (event) => {
    const { value } = event.target;
    setSearchQuery(value);
    if (onSearch) {
      onSearch(value);
    }
  };

const handleOpenDateRange = () => {
  setPendingRange({
    start: fromDate || '',
    end: toDate || '',
  });
  setShowDateRangeModal(true);
};

const handlePendingRangeChange = (event) => {
  const { name, value } = event.target;
  setPendingRange((prev) => ({
    ...prev,
    [name]: value,
  }));
};

const handleCancelDateRange = () => {
  setPendingRange({
    start: fromDate || '',
    end: toDate || '',
  });
  setShowDateRangeModal(false);
};

const handleApplyDateRange = () => {
  if (onDateChange) {
    if (pendingRange.start) {
      onDateChange('fromDate', pendingRange.start);
    }
    if (pendingRange.end) {
      onDateChange('toDate', pendingRange.end);
    }
  }
  setShowDateRangeModal(false);
};

  // Modal state for confirmation
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null); // 'approve' or 'reject'
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Modal state for create request
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    userName: '',
    amount: '',
    paymentMode: '',
    referenceNumber: '',
    bankName: '',
    remarks: ''
  });

  const isCreateDisabled = !formData.amount || !formData.paymentMode || !formData.bankName;

  // T-PIN verification state
  const [showTPinModal, setShowTPinModal] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  
  // T-PIN and reason state for approve/reject
  const [pendingAction, setPendingAction] = useState(null);
  const [actionReason, setActionReason] = useState('');

  const columns = useMemo(() => {
    const baseColumns = [
      { key: 'id', label: 'ID' },
      { key: 'date', label: 'Request Date' },
      { key: 'amount', label: 'Amount (₹)' },
      { key: 'mode', label: 'Mode' },
      { key: 'orderId', label: 'Order ID' },
      { key: 'utr', label: 'UTR' },
      { key: 'bank', label: 'Bank' },
      { key: 'transDate', label: 'Transaction Date' },
      { key: 'remark', label: 'Remark' },
      { key: 'reason', label: 'Reason' },
      { key: 'parentId', label: 'Parent ID' },
      { key: 'userId', label: 'User ID' },
      { key: 'status', label: 'Status' },
      { key: 'approvedBy', label: 'Approved By' },
      { key: 'approvedDate', label: 'Approved Date' },
      { key: 'recorn', label: 'Recorn' },
      { key: 'time', label: 'Time' }
    ];

    if (showActions) {
      return [{ key: 'actions', label: 'Actions' }, ...baseColumns];
    }

    return baseColumns;
  }, [showActions]);

  const cellPaddingStyle = denseRows
    ? { paddingTop: '0.4rem', paddingBottom: '0.4rem' }
    : { paddingTop: '1.1rem', paddingBottom: '1.1rem' };

  // Filter data based on search
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Global search
    if (searchQuery) {
      filtered = filtered.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    return filtered;
  }, [data, searchQuery]);

  const displayData = filteredData;
  const startIndex = totalRecords === 0
    ? 0
    : Math.min(safeCurrentPage * pageSize + 1, totalRecords);
  const endIndex = totalRecords === 0
    ? 0
    : Math.min(safeCurrentPage * pageSize + displayData.length, totalRecords);

  const handlePageRequest = (page) => {
    if (totalPages <= 0) {
      return;
    }
    if (!onPageChange) {
      return;
    }

    const nextPage = Math.min(Math.max(page, 0), Math.max(totalPages - 1, 0));

    if (nextPage === safeCurrentPage || Number.isNaN(nextPage)) {
      return;
    }

    onPageChange(nextPage);
  };


  // Export to Excel
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'TopUpRequests');
    XLSX.writeFile(workbook, `TopUpRequests_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Handle Approve/Reject actions
  const handleActionClick = (request, action) => {
    setSelectedRequest(request);
    setModalAction(action);
    setActionReason('');
    setShowModal(true);
  };

  const handleConfirmAction = () => {
    if (!selectedRequest) return;
    
    // Validate reason for reject action
    if (modalAction === 'reject' && !actionReason.trim()) {
      showToast.error('Please provide a reason for rejection');
      return;
    }

    // Store pending action details
    setPendingAction({
      request: selectedRequest,
      action: modalAction,
      reason: actionReason
    });
    
    // Close confirmation modal and show T-PIN modal
    setShowModal(false);
    setTimeout(() => {
      setShowTPinModal(true);
    }, 300);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
    setModalAction(null);
    setActionReason('');
  };

  // Handle Create Request Form
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateRequest = () => {
    // Validate form
    if (!formData.amount || !formData.paymentMode || !formData.bankName) {
      return;
    }

    // Generate new request ID
    const newId = `TUR${String(data.length + 1).padStart(5, '0')}`;
    
    // Create new request object and store it temporarily
    const newRequest = {
      id: newId,
      userName: formData.userName,
      amount: Number(formData.amount),
      requestDate: new Date().toISOString().split('T')[0],
      status: 'Pending',
      actionDate: null,
      paymentMode: formData.paymentMode,
      referenceNumber: formData.referenceNumber,
      bankName: formData.bankName,
      remarks: formData.remarks
    };

    setPendingRequest(newRequest);
    setShowCreateModal(false);
    
    // Show T-PIN modal
    setTimeout(() => {
      setShowTPinModal(true);
    }, 300);
  };

  const handleTPinVerify = async ({ tpin: tpinValue }) => {
    // Simulate T-PIN verification
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // Mock validation - accept "1234" as valid T-PIN
        if (tpinValue === '1234' || tpinValue.length === 4) {
          resolve();
          // Show success toast
          showToast.verificationSuccess(toastMessages.tpinSuccess);
          
          // Handle pending action (approve/reject)
          if (pendingAction) {
            const updatedData = data.map(item => {
              if (item.id === pendingAction.request.id) {
                return {
                  ...item,
                  status: pendingAction.action === 'approve' ? 'Approved' : 'Rejected',
                  actionDate: new Date().toISOString().split('T')[0],
                  reason: pendingAction.reason || ''
                };
              }
              return item;
            });

            onDataChange(updatedData);
            
            // Reset states
            setShowTPinModal(false);
            setPendingAction(null);
            setSelectedRequest(null);
            setModalAction(null);
            setActionReason('');
            
            // Show success toast
            setTimeout(() => {
              if (pendingAction.action === 'approve') {
                showToast.transactionSuccess('Request approved successfully!');
              } else {
                showToast.transactionSuccess('Request rejected successfully!');
              }
            }, 500);
          }
          // Add new request to data
          else if (pendingRequest) {
            onDataChange([pendingRequest, ...data]);

            // Close T-PIN modal and reset everything
            setShowTPinModal(false);
            setPendingRequest(null);
            
            // Reset form
            setFormData({
              userName: '',
              amount: '',
              paymentMode: '',
              referenceNumber: '',
              bankName: '',
              remarks: ''
            });

            // Show success toast notification
            setTimeout(() => {
              showToast.transactionSuccess(toastMessages.topUpSuccess);
            }, 500);
          }
        } else {
          showToast.error(toastMessages.tpinFailed);
          reject(new Error('Invalid T-PIN. Please try again.'));
        }
      }, 1000);
    });
  };

  const handleCancelTPinModal = () => {
    setShowTPinModal(false);
    setPendingRequest(null);
    setPendingAction(null);
    setActionReason('');
    showToast.info(toastMessages.cancelled);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setFormData({
      userName: '',
      amount: '',
      paymentMode: '',
      referenceNumber: '',
      bankName: '',
      remarks: ''
    });
  };

  return (
    <div className="user-table-container">
      {/* Header with Title, Search and Export */}
      <Row className="mb-3 g-4 align-items-center flex-nowrap" style={{ rowGap: '0.5rem' }}>
        <Col xs="auto">
          <div className="d-flex align-items-center gap-2">
            <h4 className="mb-0" style={{ color: '#1e293b', fontWeight: '700', fontSize: '1.5rem' }}>
              Top Up Requests
            </h4>
            {allowCreateButton && showActions && (
              <Button 
                size="sm"
                variant="outline-primary" 
                onClick={() => navigate('/reports/top-up-history')}
                style={{
                  padding: '0.35rem 0.75rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                <i className="bi bi-clock-history"></i> History
              </Button>
            )}
          </div>
        </Col>
        <Col xs="auto">
          <InputGroup style={{ minWidth: '260px' }}>
            <InputGroup.Text>
              <i className="bi bi-calendar-range"></i>
            </InputGroup.Text>
            <Form.Control
              type="text"
              readOnly
              value={fromDate && toDate ? `${fromDate} → ${toDate}` : 'Select date range'}
              onClick={handleOpenDateRange}
              placeholder="Select date range"
              style={{ cursor: 'pointer', minWidth: '200px' }}
            />
          </InputGroup>
        </Col>
        <Col xs="auto">
          <InputGroup style={{ minWidth: '260px' }}>
            <InputGroup.Text>
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchInputChange}
            />
          </InputGroup>
        </Col>
        {allowCreateButton && (
          <Col xs="auto">
            <Button 
              size="sm"
              onClick={() => setShowCreateModal(true)}
              style={{
                padding: '0.4rem 1rem 0.5rem 1rem',
                fontSize: '0.875rem',
                height: 'auto',
                backgroundColor: 'transparent',
                color: '#3b82f6',
                border: '2px solid #3b82f6',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#3b82f6';
              }}
            >
              <i className="bi bi-plus-circle me-1"></i> Top Up Balance
            </Button>
          </Col>
        )}
        <Col xs="auto">
          <Button 
            size="sm"
            variant="success" 
            onClick={exportToExcel}
            style={{
              padding: '0.4rem 1rem 0.5rem 1rem',
              fontSize: '0.875rem',
              height: 'auto'
            }}
          >
            <i className="bi bi-file-earmark-excel me-1"></i> Excel
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive" style={{ background: '#f0f2f5', borderRadius: '12px', padding: '0' }}>
        <Table bordered hover className="modern-table" style={{ background: '#f0f2f5' }}>
          <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.length > 0 ? (
              displayData.map((row, index) => (
                <tr key={index} style={{ background: '#f0f2f5' }}>
                  {showActions && (
                  <td style={cellPaddingStyle}>
                    <div className="d-flex gap-3 justify-content-center align-items-center">
                        <Button 
                          size="sm" 
                          title="Approve"
                          onClick={() => handleActionClick(row, 'approve')}
                          style={{
                            backgroundColor: 'transparent',
                            border: '1px solid #198754',
                            color: '#198754',
                            fontWeight: '500'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#198754';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#198754';
                          }}
                        >
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          title="Reject"
                          onClick={() => handleActionClick(row, 'reject')}
                          style={{
                            backgroundColor: 'transparent',
                            border: '1px solid #dc3545',
                            color: '#dc3545',
                            fontWeight: '500'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#dc3545';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#dc3545';
                          }}
                        >
                          Reject
                        </Button>
                      </div>
                    </td>
                  )}
                  <td style={cellPaddingStyle}>{row?.id ?? '-'}</td>
                  <td style={cellPaddingStyle}>{row?.date ?? '-'}</td>
                  <td className="fw-bold text-success" style={cellPaddingStyle}>
                    {row?.amount !== undefined && row?.amount !== null
                      ? `₹${Number(row.amount).toLocaleString()}`
                      : '-'}
                  </td>
                  <td style={cellPaddingStyle}>{row?.mode ?? '-'}</td>
                  <td style={cellPaddingStyle}>{row?.orderId ?? '-'}</td>
                  <td style={cellPaddingStyle}>{row?.utr ?? '-'}</td>
                  <td style={cellPaddingStyle}>{row?.bank ?? '-'}</td>
                  <td style={cellPaddingStyle}>{row?.transDate ?? '-'}</td>
                  <td style={cellPaddingStyle}>{row?.remark ?? '-'}</td>
                  <td style={cellPaddingStyle}>{row?.reason ?? '-'}</td>
                  <td style={cellPaddingStyle}>
                    {row?.parentId
                      ? [row.parentId.mobile]
                          .filter(Boolean)
                          .join(' / ')
                      : '-'}
                  </td>
                  <td style={cellPaddingStyle}>
                    {row?.userId
                      ? [row.userId.mobile]
                          .filter(Boolean)
                          .join(' / ')
                      : '-'}
                  </td>
                  <td style={cellPaddingStyle}>{row?.status ?? '-'}</td>
                  <td style={cellPaddingStyle}>{row?.approvedBy ?? '-'}</td>
                  <td style={cellPaddingStyle}>{row?.approvedDate ?? '-'}</td>
                  <td style={cellPaddingStyle}>{row?.recorn ?? '-'}</td>
                  <td style={cellPaddingStyle}>{row?.time ?? '-'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                  <span className="text-muted">No requests found</span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <Row className="mt-3 align-items-center">
        <Col md={6}>
          <span className="text-muted">
            Showing {startIndex || 0} to {endIndex || 0} of {totalRecords} entries
          </span>
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-center">
          <Pagination className="mb-0">
          <Pagination.First 
            onClick={() => handlePageRequest(0)} 
            disabled={safeCurrentPage <= 0}
            />
            <Pagination.Prev 
            onClick={() => handlePageRequest(safeCurrentPage - 1)} 
            disabled={safeCurrentPage <= 0}
            />
            
          {paginationPages.map((page, index) => {
            if (page === 'start-ellipsis' || page === 'end-ellipsis') {
              return <Pagination.Ellipsis key={`${page}-${index}`} disabled />;
            }

            return (
              <Pagination.Item
                key={page}
                active={page === safeCurrentPage}
                onClick={() => handlePageRequest(page)}
              >
                {page + 1}
              </Pagination.Item>
            );
          })}
            
            <Pagination.Next 
            onClick={() => handlePageRequest(safeCurrentPage + 1)} 
            disabled={safeCurrentPage >= totalPages - 1}
            />
            <Pagination.Last 
              onClick={() => handlePageRequest(totalPages - 1)} 
            disabled={safeCurrentPage >= totalPages - 1}
            />
          </Pagination>
        </Col>
      </Row>

      {showActions && (
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalAction === 'approve' ? 'Approve Request' : 'Reject Request'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedRequest && (
              <div>
                <p>Are you sure you want to <strong>{modalAction}</strong> this request?</p>
                <div className="bg-light p-3 rounded mb-3">
                  <p className="mb-2"><strong>Request ID:</strong> {selectedRequest.id}</p>
                  <p className="mb-2"><strong>User Name:</strong> {selectedRequest.userName}</p>
                  <p className="mb-0"><strong>Amount:</strong> ₹{selectedRequest.amount.toLocaleString()}</p>
                </div>
                
                <Form.Group className="mt-3">
                  <Form.Label>
                    Reason {modalAction === 'reject' ? <span className="text-danger">*</span> : <span className="text-muted">(Optional)</span>}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder={modalAction === 'approve' ? 'Enter reason for approval (optional)' : 'Enter reason for rejection (required)'}
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                  />
                  {modalAction === 'reject' && !actionReason.trim() && (
                    <Form.Text className="text-danger">
                      Please provide a reason for rejection
                    </Form.Text>
                  )}
                </Form.Group>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button 
              variant={modalAction === 'approve' ? 'success' : 'danger'}
              onClick={handleConfirmAction}
            >
              Continue
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {allowCreateButton && (
        <Modal show={showCreateModal} onHide={handleCloseCreateModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-plus-circle me-2"></i>
              Top Up Balance
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                {/* <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>User Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="userName"
                      value={formData.userName}
                      onChange={handleFormChange}
                      placeholder="Enter user name"
                      required
                    />
                  </Form.Group>
                </Col> */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Amount (₹) <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleFormChange}
                      placeholder="Enter amount"
                      min="0"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Bank Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Bank</option>
                      <option value="State Bank of India">State Bank of India</option>
                      <option value="HDFC Bank">HDFC Bank</option>
                      <option value="ICICI Bank">ICICI Bank</option>
                      <option value="Axis Bank">Axis Bank</option>
                      <option value="Kotak Mahindra Bank">Kotak Mahindra Bank</option>
                      <option value="Punjab National Bank">Punjab National Bank</option>
                      <option value="Bank of Baroda">Bank of Baroda</option>
                      <option value="Canara Bank">Canara Bank</option>
                      <option value="Union Bank of India">Union Bank of India</option>
                      <option value="IndusInd Bank">IndusInd Bank</option>
                      <option value="Yes Bank">Yes Bank</option>
                      <option value="IDFC First Bank">IDFC First Bank</option>
                      <option value="Federal Bank">Federal Bank</option>
                      <option value="Bank of India">Bank of India</option>
                      <option value="Central Bank of India">Central Bank of India</option>
                      <option value="Indian Bank">Indian Bank</option>
                      <option value="Indian Overseas Bank">Indian Overseas Bank</option>
                      <option value="UCO Bank">UCO Bank</option>
                      <option value="Bank of Maharashtra">Bank of Maharashtra</option>
                      <option value="Punjab & Sind Bank">Punjab & Sind Bank</option>
                      <option value="Other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Mode <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="paymentMode"
                      value={formData.paymentMode}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Payment Mode</option>
                      <option value="NEFT">NEFT</option>
                      <option value="RTGS">RTGS</option>
                      <option value="IMPS">IMPS</option>
                      <option value="UPI">UPI</option>
                      <option value="Cash Deposit">Cash Deposit</option>
                      <option value="Cheque">Cheque</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Reference Number <span className="text-muted">(Optional)</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="referenceNumber"
                      value={formData.referenceNumber}
                      onChange={handleFormChange}
                      placeholder="Enter reference/transaction number"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Remarks <span className="text-muted">(Optional)</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleFormChange}
                      placeholder="Enter any additional remarks"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              onClick={handleCloseCreateModal}
              style={{
                padding: '0.5rem 1.5rem',
                fontSize: '0.9rem',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: '2px solid #6b7280',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#6b7280';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateRequest}
              disabled={isCreateDisabled}
              style={{
                padding: '0.5rem 1.5rem',
                fontSize: '0.9rem',
                backgroundColor: 'transparent',
                color: isCreateDisabled 
                  ? '#94a3b8' 
                  : '#3b82f6',
                border: `2px solid ${isCreateDisabled 
                  ? '#94a3b8' 
                  : '#3b82f6'}`,
                fontWeight: '500',
                cursor: isCreateDisabled 
                  ? 'not-allowed' 
                  : 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!isCreateDisabled) {
                  e.currentTarget.style.backgroundColor = '#3b82f6';
                  e.currentTarget.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (!isCreateDisabled) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#3b82f6';
                }
              }}
            >
              Submit
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* T-PIN Modal */}
      <TPinModal
        show={showTPinModal}
        onHide={handleCancelTPinModal}
        onVerify={handleTPinVerify}
        title="Enter T-PIN"
        message={
          pendingAction 
            ? `Please enter your 4-digit T-PIN to ${pendingAction.action} the request`
            : "Please enter your 4-digit T-PIN to proceed with the top-up request"
        }
      />

      <Modal show={showDateRangeModal} onHide={handleCancelDateRange} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Date Range</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group controlId="topup-date-range-start">
                  <Form.Label>From Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="start"
                    value={pendingRange.start}
                    max={pendingRange.end || new Date().toISOString().split('T')[0]}
                    onChange={handlePendingRangeChange}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="topup-date-range-end">
                  <Form.Label>To Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="end"
                    value={pendingRange.end}
                    min={pendingRange.start}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={handlePendingRangeChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDateRange}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleApplyDateRange}
            disabled={!pendingRange.start || !pendingRange.end}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TopUpRequestTable;

